import Phaser from 'phaser'
import { SpellSystem } from '../systems/SpellSystem'
import { InventorySystem } from '../systems/InventorySystem'
import { SpellType, SPELLS, INVENTORY_EVENTS } from '../types/GameTypes'

type ButtonType = SpellType | 'healthPotion' | 'manaPotion'

interface ActionButton {
  container: Phaser.GameObjects.Container
  background: Phaser.GameObjects.Arc
  icon: Phaser.GameObjects.Graphics
  cooldownOverlay: Phaser.GameObjects.Graphics
  manaText: Phaser.GameObjects.Text
  spellType?: SpellType
  buttonType: ButtonType
}

export class ActionButtons {
  private scene: Phaser.Scene
  private spellSystem: SpellSystem
  private inventory: InventorySystem | null = null

  private container: Phaser.GameObjects.Container
  private buttons: ActionButton[] = []

  // Potion button state
  private currentPotionType: 'healthPotion' | 'manaPotion' = 'healthPotion'
  private potionButton: ActionButton | null = null

  private buttonSize: number = 56
  private buttonSpacing: number = 70
  private padding: number = 20

  constructor(
    scene: Phaser.Scene,
    spellSystem: SpellSystem,
    inventory?: InventorySystem
  ) {
    this.scene = scene
    this.spellSystem = spellSystem
    this.inventory = inventory || null

    // Create main container
    this.container = scene.add.container(0, 0)
    this.container.setDepth(1000)

    // Create buttons
    this.createButtons()

    // Position buttons
    this.updatePosition()

    // Handle resize
    scene.scale.on('resize', this.updatePosition, this)

    // Listen for inventory changes to update potion counts
    if (this.inventory) {
      scene.events.on(INVENTORY_EVENTS.INVENTORY_CHANGED, this.updatePotionButton, this)
    }
  }

  private createButtons(): void {
    const spellTypes: SpellType[] = ['attack', 'heal', 'fireBolt']

    spellTypes.forEach((spellType, index) => {
      const button = this.createSpellButton(spellType, index)
      this.buttons.push(button)
      this.container.add(button.container)
    })

    // Add potion button if inventory is available
    if (this.inventory) {
      const potionButton = this.createPotionButton(spellTypes.length)
      this.potionButton = potionButton
      this.buttons.push(potionButton)
      this.container.add(potionButton.container)
    }
  }

  private createSpellButton(spellType: SpellType, index: number): ActionButton {
    const spell = SPELLS[spellType]
    const x = -index * this.buttonSpacing
    const y = 0

    // Button container
    const buttonContainer = this.scene.add.container(x, y)

    // Background circle
    const background = this.scene.add.circle(0, 0, this.buttonSize / 2, 0x2d3748)
    background.setStrokeStyle(3, 0x4a5568)
    background.setInteractive({ useHandCursor: true })

    // Icon (drawn with graphics)
    const icon = this.scene.add.graphics()
    this.drawIcon(icon, spellType)

    // Cooldown overlay
    const cooldownOverlay = this.scene.add.graphics()

    // Mana cost text
    const manaText = this.scene.add.text(0, this.buttonSize / 2 + 8,
      spell.manaCost > 0 ? `${spell.manaCost}` : '', {
        fontSize: '12px',
        color: '#63b3ed',
        fontFamily: 'Arial'
      }).setOrigin(0.5)

    // Add to container
    buttonContainer.add([background, icon, cooldownOverlay, manaText])

    // Input events
    background.on('pointerdown', () => this.onSpellButtonPress(spellType))
    background.on('pointerover', () => {
      background.setFillStyle(0x4a5568)
    })
    background.on('pointerout', () => {
      background.setFillStyle(0x2d3748)
    })

    return {
      container: buttonContainer,
      background,
      icon,
      cooldownOverlay,
      manaText,
      spellType,
      buttonType: spellType
    }
  }

  private createPotionButton(index: number): ActionButton {
    const x = -index * this.buttonSpacing
    const y = 0

    // Button container
    const buttonContainer = this.scene.add.container(x, y)

    // Background circle
    const background = this.scene.add.circle(0, 0, this.buttonSize / 2, 0x2d3748)
    background.setStrokeStyle(3, 0x4a5568)
    background.setInteractive({ useHandCursor: true })

    // Icon (drawn with graphics)
    const icon = this.scene.add.graphics()
    this.drawPotionIcon(icon, this.currentPotionType)

    // Cooldown overlay (not used for potions but kept for consistency)
    const cooldownOverlay = this.scene.add.graphics()

    // Count text (shows number of potions)
    const countText = this.scene.add.text(0, this.buttonSize / 2 + 8, '0', {
      fontSize: '12px',
      color: '#22c55e',
      fontFamily: 'Arial'
    }).setOrigin(0.5)

    // Add to container
    buttonContainer.add([background, icon, cooldownOverlay, countText])

    // Input events
    background.on('pointerdown', () => this.onPotionButtonPress())
    background.on('pointerover', () => {
      background.setFillStyle(0x4a5568)
    })
    background.on('pointerout', () => {
      background.setFillStyle(0x2d3748)
    })

    // Long press to switch potion type
    let longPressTimer: Phaser.Time.TimerEvent | null = null
    background.on('pointerdown', () => {
      longPressTimer = this.scene.time.delayedCall(500, () => {
        this.cyclePotionType()
      })
    })
    background.on('pointerup', () => {
      if (longPressTimer) {
        longPressTimer.destroy()
        longPressTimer = null
      }
    })
    background.on('pointerout', () => {
      if (longPressTimer) {
        longPressTimer.destroy()
        longPressTimer = null
      }
    })

    return {
      container: buttonContainer,
      background,
      icon,
      cooldownOverlay,
      manaText: countText,
      buttonType: this.currentPotionType
    }
  }

  /**
   * Cycle between health and mana potions
   */
  private cyclePotionType(): void {
    this.currentPotionType = this.currentPotionType === 'healthPotion' ? 'manaPotion' : 'healthPotion'

    if (this.potionButton) {
      this.potionButton.buttonType = this.currentPotionType
      this.potionButton.icon.clear()
      this.drawPotionIcon(this.potionButton.icon, this.currentPotionType)
      this.updatePotionButton()

      // Visual feedback for switch
      this.scene.tweens.add({
        targets: this.potionButton.container,
        scale: 1.1,
        duration: 100,
        yoyo: true
      })
    }
  }

  /**
   * Update potion button count and availability
   */
  private updatePotionButton(): void {
    if (!this.potionButton || !this.inventory) return

    let count = 0
    let color = '#22c55e'

    if (this.currentPotionType === 'healthPotion') {
      count = this.inventory.getHealthPotionCount()
      color = '#ef4444'
    } else {
      count = this.inventory.getManaPotionCount()
      color = '#3b82f6'
    }

    this.potionButton.manaText.setText(count.toString())
    this.potionButton.manaText.setColor(count > 0 ? color : '#6b7280')

    // Dim button if no potions
    if (count === 0) {
      this.potionButton.background.setAlpha(0.5)
      this.potionButton.icon.setAlpha(0.5)
    } else {
      this.potionButton.background.setAlpha(1)
      this.potionButton.icon.setAlpha(1)
    }
  }

  private drawIcon(graphics: Phaser.GameObjects.Graphics, spellType: SpellType): void {
    graphics.clear()

    switch (spellType) {
      case 'attack':
        // Sword icon
        graphics.lineStyle(3, 0xfbbf24)
        // Blade
        graphics.beginPath()
        graphics.moveTo(-8, 8)
        graphics.lineTo(10, -10)
        graphics.strokePath()
        // Cross guard
        graphics.beginPath()
        graphics.moveTo(-2, 2)
        graphics.lineTo(4, -4)
        graphics.moveTo(2, 4)
        graphics.lineTo(-4, -2)
        graphics.strokePath()
        // Handle
        graphics.lineStyle(4, 0x8b4513)
        graphics.beginPath()
        graphics.moveTo(-8, 8)
        graphics.lineTo(-12, 12)
        graphics.strokePath()
        break

      case 'heal':
        // Heart/cross icon
        graphics.fillStyle(0xef4444)
        // Heart shape using circles and triangle
        graphics.fillCircle(-5, -3, 6)
        graphics.fillCircle(5, -3, 6)
        graphics.fillTriangle(-11, 0, 11, 0, 0, 14)
        break

      case 'fireBolt':
        // Flame icon - simplified with shapes
        // Outer flame (orange)
        graphics.fillStyle(0xff6600)
        graphics.fillTriangle(0, -14, -10, 8, 10, 8)
        graphics.fillCircle(-5, 4, 6)
        graphics.fillCircle(5, 4, 6)
        graphics.fillCircle(0, 0, 5)
        // Inner flame (yellow)
        graphics.fillStyle(0xfbbf24)
        graphics.fillTriangle(0, -8, -5, 6, 5, 6)
        graphics.fillCircle(0, 2, 4)
        break
    }
  }

  private drawPotionIcon(graphics: Phaser.GameObjects.Graphics, potionType: 'healthPotion' | 'manaPotion'): void {
    graphics.clear()

    const color = potionType === 'healthPotion' ? 0xef4444 : 0x3b82f6
    const highlightColor = potionType === 'healthPotion' ? 0xfca5a5 : 0x93c5fd

    // Potion bottle body
    graphics.fillStyle(color)
    graphics.fillRoundedRect(-10, -6, 20, 18, 4)

    // Highlight
    graphics.fillStyle(highlightColor)
    graphics.fillRect(-7, -3, 6, 10)

    // Bottle neck
    graphics.fillStyle(0x9ca3af)
    graphics.fillRect(-4, -12, 8, 6)

    // Cork
    graphics.fillStyle(0xa16207)
    graphics.fillRect(-3, -14, 6, 4)
  }

  private onSpellButtonPress(spellType: SpellType): void {
    const success = this.spellSystem.cast(spellType)

    if (success) {
      // Find button and animate
      const button = this.buttons.find(b => b.spellType === spellType)
      if (button) {
        // Press animation
        this.scene.tweens.add({
          targets: button.container,
          scale: 0.9,
          duration: 50,
          yoyo: true
        })
      }
    } else {
      // Failed cast - flash red
      const button = this.buttons.find(b => b.spellType === spellType)
      if (button) {
        button.background.setStrokeStyle(3, 0xef4444)
        this.scene.time.delayedCall(200, () => {
          button.background.setStrokeStyle(3, 0x4a5568)
        })
      }
    }
  }

  private onPotionButtonPress(): void {
    if (!this.inventory || !this.potionButton) return

    let success = false

    if (this.currentPotionType === 'healthPotion') {
      success = this.inventory.useHealthPotion()
    } else {
      success = this.inventory.useManaPotion()
    }

    if (success) {
      // Press animation
      this.scene.tweens.add({
        targets: this.potionButton.container,
        scale: 0.9,
        duration: 50,
        yoyo: true
      })
      this.updatePotionButton()
    } else {
      // Failed - flash red
      this.potionButton.background.setStrokeStyle(3, 0xef4444)
      this.scene.time.delayedCall(200, () => {
        if (this.potionButton) {
          this.potionButton.background.setStrokeStyle(3, 0x4a5568)
        }
      })
    }
  }

  /**
   * Update cooldown overlays
   */
  public update(): void {
    this.buttons.forEach(button => {
      // Skip non-spell buttons (like potion button)
      if (!button.spellType) {
        return
      }

      const spellType = button.spellType
      const cooldownPercent = this.spellSystem.getCooldownPercent(spellType)
      const hasEnoughMana = this.spellSystem.hasEnoughMana(spellType)

      // Update cooldown overlay
      button.cooldownOverlay.clear()

      if (cooldownPercent > 0) {
        // Draw cooldown arc
        button.cooldownOverlay.fillStyle(0x000000, 0.6)
        button.cooldownOverlay.slice(
          0, 0,
          this.buttonSize / 2,
          -Math.PI / 2,
          -Math.PI / 2 + (cooldownPercent * Math.PI * 2),
          true
        )
        button.cooldownOverlay.fillPath()
      }

      // Dim if not enough mana
      if (!hasEnoughMana && SPELLS[spellType].manaCost > 0) {
        button.background.setAlpha(0.5)
        button.icon.setAlpha(0.5)
        button.manaText.setColor('#ef4444') // Red when can't afford
      } else {
        button.background.setAlpha(1)
        button.icon.setAlpha(1)
        button.manaText.setColor('#63b3ed') // Blue when affordable
      }
    })
  }

  /**
   * Update button positions on resize
   */
  private updatePosition(): void {
    const width = this.scene.scale.width
    const height = this.scene.scale.height

    // Position in bottom-right
    this.container.setPosition(
      width - this.padding - this.buttonSize / 2,
      height - this.padding - this.buttonSize / 2 - 30 // Extra offset for mana text
    )
  }

  /**
   * Set inventory reference (called when inventory becomes available)
   */
  public setInventory(inventory: InventorySystem): void {
    this.inventory = inventory
    this.scene.events.on(INVENTORY_EVENTS.INVENTORY_CHANGED, this.updatePotionButton, this)

    // Create potion button if not exists
    if (!this.potionButton) {
      const potionButton = this.createPotionButton(this.buttons.length)
      this.potionButton = potionButton
      this.buttons.push(potionButton)
      this.container.add(potionButton.container)
    }

    this.updatePotionButton()
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.scale.off('resize', this.updatePosition, this)
    if (this.inventory) {
      this.scene.events.off(INVENTORY_EVENTS.INVENTORY_CHANGED, this.updatePotionButton, this)
    }
    this.container.destroy()
  }
}
